import { getPointHubConfig, getPointHubConfigValidationErrors } from '../config';
import { acquirePointHubReport, finishPointHubReport } from '../dedupe';
import { emitPointHubDiagnostic } from '../diagnostics';
import { reportNormalizedMeetingBookingSessionRecommend } from '../reporter';
import { getPointHubRuntimeSupportDetail, isPointHubRuntimeSupported } from '../sdk';
import type {
  MeetingOrderPointHubCollect,
  MeetingOrderPointHubCollectDraft,
  MeetingOrderPointHubComponentOptions,
  MeetingOrderPointHubElement,
  PointHubReportContext,
  PointHubReportResult
} from '../types';
import { getPointHubUserIdentityKey, resolvePointHubUserInfo } from '../user-context';
import { processMeetingBookingDraftDiagnostics } from './index';
import { normalizeMeetingBookingSessionRecommend } from './normalize';
import { validateMeetingBookingCollectDraft, validateMeetingBookingSessionRecommend } from './validate';

export const isPointHubCollectEnabled = (context?: PointHubReportContext): boolean => {
  const config = getPointHubConfig();
  if (!config.enabled) return false;

  const configErrors = getPointHubConfigValidationErrors(config);
  if (configErrors.length > 0) {
    emitPointHubDiagnostic({
      code: 'invalid_config',
      environment: config.environment,
      count: configErrors.length,
      detail: { fields: configErrors.join(',') }
    });
    return false;
  }

  const runtimeDetail = getPointHubRuntimeSupportDetail();
  if (!isPointHubRuntimeSupported(runtimeDetail)) {
    emitPointHubDiagnostic({
      code: 'unsupported_runtime',
      environment: config.environment,
      detail: runtimeDetail
    });
    return false;
  }

  if (!resolvePointHubUserInfo(context)) {
    emitPointHubDiagnostic({
      code: 'invalid_user_context',
      environment: config.environment
    });
    return false;
  }

  return true;
};

export const getMeetingOrderPointHubComponentOptions = (
  context?: PointHubReportContext
): MeetingOrderPointHubComponentOptions => {
  const config = getPointHubConfig();
  return {
    enabled: isPointHubCollectEnabled(context),
    platform: config.sourceType,
    entrySource: 'schedule_entry',
    debug: config.debug && process.env.NODE_ENV !== 'production'
  };
};

export const bindMeetingOrderPointHub = (
  element: MeetingOrderPointHubElement,
  collect: MeetingOrderPointHubCollect,
  options: MeetingOrderPointHubComponentOptions
): (() => void) => {
  element.pointHubCollect = collect;
  element.pointHubPlatform = options.platform;
  element.pointHubEntrySource = options.entrySource;
  element.pointHubDebug = options.debug;
  element.pointHubCollectEnabled = options.enabled;

  return () => {
    element.pointHubCollectEnabled = false;
    element.pointHubCollect = undefined;
    element.pointHubDebug = false;
  };
};

const emitValidationErrors = (eventId: string | undefined, errors: string[]): void => {
  errors.forEach((field) => {
    emitPointHubDiagnostic({ code: 'invalid_payload', eventId, field });
  });
};

export const reportMeetingBookingSessionRecommend = async (
  draft: MeetingOrderPointHubCollectDraft,
  context?: PointHubReportContext
): Promise<PointHubReportResult | null> => {
  if (!isPointHubCollectEnabled(context)) return null;

  const draftValidation = validateMeetingBookingCollectDraft(draft);
  if (!draftValidation.valid || !draftValidation.value) {
    emitValidationErrors(draft && draft.eventId, draftValidation.errors);
    return { status: 'skipped', eventId: draft && draft.eventId, code: 'invalid_draft' };
  }

  const normalized = normalizeMeetingBookingSessionRecommend(draftValidation.value);
  processMeetingBookingDraftDiagnostics(draftValidation.value.diagnostics, normalized.event.eventId, normalized.droppedFields);
  const eventValidation = validateMeetingBookingSessionRecommend(normalized.event);
  if (!eventValidation.valid || !eventValidation.value) {
    emitValidationErrors(normalized.event.eventId, eventValidation.errors);
    return { status: 'skipped', eventId: normalized.event.eventId, code: 'invalid_payload' };
  }

  const userInfo = resolvePointHubUserInfo(context);
  if (!userInfo) return { status: 'skipped', eventId: normalized.event.eventId, code: 'invalid_user_context' };
  const reportKey = acquirePointHubReport(eventValidation.value, getPointHubUserIdentityKey(userInfo));
  if (!reportKey) {
    emitPointHubDiagnostic({ code: 'duplicated_local', eventId: normalized.event.eventId });
    return { status: 'duplicated_local', eventId: normalized.event.eventId, code: 'duplicated_local' };
  }

  try {
    return await reportNormalizedMeetingBookingSessionRecommend(
      eventValidation.value,
      { resolvedUserInfo: userInfo },
      getPointHubConfig()
    );
  } catch (_error) {
    emitPointHubDiagnostic({ code: 'bridge_report_failed', eventId: normalized.event.eventId });
    return { status: 'rejected', eventId: normalized.event.eventId, code: 'bridge_report_failed' };
  } finally {
    finishPointHubReport(reportKey);
  }
};

export const createMeetingOrderPointHubBridge = (
  context?: PointHubReportContext
): MeetingOrderPointHubCollect => {
  return async (draft) => {
    try {
      await reportMeetingBookingSessionRecommend(draft, context);
    } catch (_error) {
      emitPointHubDiagnostic({ code: 'bridge_unhandled_error', eventId: draft && draft.eventId });
    }
  };
};

export const createMeetingOrderPointHubSuccessResult = <T extends { meetingNo: string }>(meeting: T) => ({
  success: true as const,
  data: [meeting]
});

export const completeMeetingOrderPointHubSuccess = <T extends { meetingNo: string }>(
  meeting: T,
  resolve: (result: { success: true; data: T[] }) => void,
  afterComponentHandledSuccess: () => void
): void => {
  resolve(createMeetingOrderPointHubSuccessResult(meeting));

  // The component emits its terminal draft after awaiting confirm(). Let that
  // continuation run before navigation unmounts the custom element.
  setTimeout(afterComponentHandledSuccess, 0);
};
